import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import supabase from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiArrowLeft, FiAlertCircle, FiEdit, FiTrash2, FiPlus, FiX, FiUsers, FiClock } = FiIcons;

const Academy = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const navigate = useNavigate();
  
  const emptyGroup = {
    name: '',
    ages: '',
    description: '',
    schedule: '',
    capacity: ''
  };
  
  const [formData, setFormData] = useState(emptyGroup);
  
  useEffect(() => {
    fetchGroups();
  }, []);
  
  const fetchGroups = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('academy_groups_aox1234')
        .select('*')
        .order('created_at', { ascending: true });
        
      if (error) throw error;
      
      setGroups(data || []);
    } catch (err) {
      console.error('Error fetching academy groups:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    
    try {
      // Prepare data for submission
      const groupData = {
        ...formData,
        updated_at: new Date().toISOString()
      };
      
      let result;
      
      if (editingId) {
        // Update existing group
        result = await supabase
          .from('academy_groups_aox1234')
          .update(groupData)
          .eq('id', editingId);
      } else {
        // Insert new group
        result = await supabase
          .from('academy_groups_aox1234')
          .insert([{ ...groupData, created_at: new Date().toISOString() }]);
      }
      
      if (result.error) throw result.error;
      
      setMessage({ 
        type: 'success', 
        text: editingId 
          ? 'Η ομάδα ενημερώθηκε επιτυχώς!' 
          : 'Η νέα ομάδα προστέθηκε επιτυχώς!' 
      });
      
      // Reset form and refresh groups list
      resetForm();
      fetchGroups();
    } catch (err) {
      console.error('Error saving academy group:', err);
      setMessage({ 
        type: 'error', 
        text: `Σφάλμα: ${err.message || 'Προέκυψε πρόβλημα κατά την αποθήκευση'}` 
      });
    }
  };
  
  const handleEdit = (group) => {
    setFormData(group);
    setEditingId(group.id);
    setShowForm(true);
    window.scrollTo(0, 0);
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm('Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτή την ομάδα;')) return;
    
    try {
      const { error } = await supabase
        .from('academy_groups_aox1234')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      setMessage({ type: 'success', text: 'Η ομάδα διαγράφηκε επιτυχώς!' });
      fetchGroups();
    } catch (err) {
      console.error('Error deleting academy group:', err);
      setMessage({ 
        type: 'error', 
        text: `Σφάλμα κατά τη διαγραφή: ${err.message}` 
      });
    }
  };
  
  const resetForm = () => {
    setFormData(emptyGroup);
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              >
                <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
              </button>
              <h1 className="text-lg font-semibold text-gray-900">Academy</h1>
            </div>
            <button
              onClick={() => setShowForm(!showForm)}
              className={`flex items-center px-4 py-2 ${showForm ? 'bg-gray-600' : 'bg-primary-600'} hover:bg-primary-700 text-white rounded-md`}
            >
              <SafeIcon icon={showForm ? FiX : FiPlus} className="w-5 h-5 mr-2" />
              {showForm ? 'Ακύρωση' : 'Προσθήκη Ομάδας'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {message.text && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-md flex items-center ${
              message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}
          >
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 mr-2" />
            {message.text}
          </motion.div>
        )}

        {/* Form for adding/editing academy groups */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="bg-white rounded-lg shadow-md p-6 mb-8"
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              {editingId ? 'Επεξεργασία Ομάδας' : 'Προσθήκη Νέας Ομάδας'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Όνομα Ομάδας *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="π.χ. Μικρά Αστέρια"
                  />
                </div>
                
                <div>
                  <label htmlFor="ages" className="block text-sm font-medium text-gray-700 mb-1">
                    Ηλικίες *
                  </label>
                  <input
                    type="text"
                    id="ages"
                    name="ages"
                    required
                    value={formData.ages}
                    onChange={handleInputChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="π.χ. 5-7 ετών"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Περιγραφή
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows="3"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Περιγράψτε τους στόχους και το πρόγραμμα της ομάδας"
                ></textarea>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="schedule" className="block text-sm font-medium text-gray-700 mb-1">
                    Πρόγραμμα Προπονήσεων
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiClock} className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="schedule"
                      name="schedule"
                      value={formData.schedule}
                      onChange={handleInputChange}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="π.χ. Τρίτη & Πέμπτη 17:00-18:00"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="capacity" className="block text-sm font-medium text-gray-700 mb-1">
                    Χωρητικότητα
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiUsers} className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="capacity"
                      name="capacity"
                      value={formData.capacity}
                      onChange={handleInputChange}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="π.χ. 15 παιδιά"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                >
                  Ακύρωση
                </button>
                <button
                  type="submit"
                  className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none"
                >
                  <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                  {editingId ? 'Ενημέρωση' : 'Αποθήκευση'}
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Academy Groups List */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Ηλικιακές Ομάδες Academy</h2>
          
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Φόρτωση...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 p-4 rounded-md text-red-700">
              <p>{error}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {groups.map((group, index) => (
                <motion.div
                  key={group.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="bg-gray-50 rounded-xl shadow-md overflow-hidden relative group"
                >
                  <div className="absolute top-2 right-2 space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      onClick={() => handleEdit(group)}
                      className="p-1 bg-blue-100 rounded-full hover:bg-blue-200 text-blue-600"
                      title="Επεξεργασία"
                    >
                      <SafeIcon icon={FiEdit} className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleDelete(group.id)}
                      className="p-1 bg-red-100 rounded-full hover:bg-red-200 text-red-600"
                      title="Διαγραφή"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="bg-primary-600 text-white p-4 text-center">
                    <h3 className="text-lg font-semibold mb-1">{group.name}</h3>
                    <p className="text-primary-200 text-sm">{group.ages}</p>
                  </div>
                  
                  <div className="p-4">
                    {group.description && (
                      <p className="text-gray-700 text-sm mb-4">{group.description}</p>
                    )}
                    
                    <div className="space-y-2">
                      {group.schedule && (
                        <div className="flex items-center text-gray-600 text-sm">
                          <SafeIcon icon={FiClock} className="w-4 h-4 mr-2 text-primary-600" />
                          <span>{group.schedule}</span>
                        </div>
                      )}
                      
                      {group.capacity && (
                        <div className="flex items-center text-gray-600 text-sm">
                          <SafeIcon icon={FiUsers} className="w-4 h-4 mr-2 text-primary-600" />
                          <span>{group.capacity}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
          
          {!loading && groups.length === 0 && (
            <p className="text-gray-500 text-center py-4">Δεν υπάρχουν καταχωρημένες ομάδες Academy</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Academy;